

import React, { createContext, useContext, useReducer, useCallback } from 'react';

const AutomationContext = createContext();

const initialState = {
  // Wizard state
  activeStep: 0,
  wizardData: {
    name: '',
    description: '',
    object: '',
    trigger: '',
    isActive: true
  },
  
  // Conditions state
  conditionGroups: [],
  groupLogic: 'AND',
  
  // Actions state
  actions: [],
  // Absolute Create Task due dates present when the automation was loaded for
  // edit — unchanged stale values must not block save (matches backend).
  baselineAbsoluteDueDates: [],
  
  // Metadata cache
  objectMetadata: {},
  fieldMetadata: {},
  relationships: {},
  operators: {},
  
  // UI state
  errors: {},
  showValidationErrors: false,
  loading: {
    objects: false,
    fields: false,
    references: false
  },
  
  // Reference data cache
  referenceDataCache: {},
  referenceDataLoading: {}
};

const actionTypes = {
  SET_ACTIVE_STEP: 'SET_ACTIVE_STEP',
  UPDATE_WIZARD_DATA: 'UPDATE_WIZARD_DATA',
  SET_CONDITION_GROUPS: 'SET_CONDITION_GROUPS',
  UPDATE_CONDITION_GROUP: 'UPDATE_CONDITION_GROUP',
  ADD_CONDITION_GROUP: 'ADD_CONDITION_GROUP',
  REMOVE_CONDITION_GROUP: 'REMOVE_CONDITION_GROUP',
  SET_GROUP_LOGIC: 'SET_GROUP_LOGIC',
  SET_ACTIONS: 'SET_ACTIONS',
  ADD_ACTION: 'ADD_ACTION',
  UPDATE_ACTION: 'UPDATE_ACTION',
  REMOVE_ACTION: 'REMOVE_ACTION',
  SET_METADATA: 'SET_METADATA',
  SET_LOADING: 'SET_LOADING',
  SET_ERRORS: 'SET_ERRORS',
  SET_REFERENCE_DATA: 'SET_REFERENCE_DATA',
  RESET_WIZARD: 'RESET_WIZARD',
  LOAD_AUTOMATION: 'LOAD_AUTOMATION'
};

function automationReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_ACTIVE_STEP:
      return { ...state, activeStep: action.payload };
      
    case actionTypes.UPDATE_WIZARD_DATA:
      return {
        ...state,
        wizardData: { ...state.wizardData, ...action.payload }
      };
      
    case actionTypes.SET_CONDITION_GROUPS:
      return { ...state, conditionGroups: action.payload };
      
    case actionTypes.ADD_CONDITION_GROUP:
      return {
        ...state,
        conditionGroups: [...state.conditionGroups, action.payload]
      };
      
    case actionTypes.UPDATE_CONDITION_GROUP:
      return {
        ...state,
        conditionGroups: state.conditionGroups.map(group =>
          group.id === action.payload.groupId
            ? { ...group, ...action.payload.updates }
            : group
        )
      };
      
    case actionTypes.REMOVE_CONDITION_GROUP:
      return {
        ...state,
        conditionGroups: state.conditionGroups.filter(
          group => group.id !== action.payload
        )
      };
      
    case actionTypes.SET_GROUP_LOGIC:
      return { ...state, groupLogic: action.payload };
      
    case actionTypes.SET_ACTIONS:
      return { ...state, actions: action.payload };
      
    case actionTypes.ADD_ACTION:
      return { ...state, actions: [...state.actions, action.payload] };
      
    case actionTypes.UPDATE_ACTION:
      return {
        ...state,
        actions: state.actions.map((actionItem, index) =>
          index === action.payload.index
            ? { ...actionItem, ...action.payload.updates }
            : actionItem
        )
      };
      
    case actionTypes.REMOVE_ACTION:
      return {
        ...state,
        actions: state.actions.filter((_, index) => index !== action.payload)
      };
      
    case actionTypes.SET_METADATA:
      // Support functional updates
      const newData = typeof action.payload.data === 'function' 
        ? action.payload.data(state[action.payload.type])
        : action.payload.data;
      return {
        ...state,
        [action.payload.type]: newData
      };
      
    case actionTypes.SET_LOADING:
      return {
        ...state,
        loading: { ...state.loading, ...action.payload }
      };
      
    case actionTypes.SET_ERRORS:
      return {
        ...state,
        errors: action.payload,
        showValidationErrors: Object.keys(action.payload).length > 0
      };
      
    case actionTypes.SET_REFERENCE_DATA:
      return {
        ...state,
        referenceDataCache: {
          ...state.referenceDataCache,
          [action.payload.endpoint]: action.payload.data
        },
        referenceDataLoading: {
          ...state.referenceDataLoading,
          [action.payload.endpoint]: false
        }
      };
      
    case actionTypes.RESET_WIZARD:
      return initialState;
      
    case actionTypes.LOAD_AUTOMATION: {
      const loadedActions = action.payload.actions || [];
      const baselineAbsoluteDueDates = loadedActions
        .filter((a) => a?.type === 'Create Task')
        .map((a) => a?.parameters?.due_date)
        .filter(
          (due) =>
            due &&
            typeof due !== 'object' &&
            !String(due).startsWith('RELATIVE::')
        )
        .map(String);
      return {
        ...state,
        wizardData: action.payload.wizardData,
        conditionGroups: action.payload.conditionGroups || [],
        groupLogic: action.payload.groupLogic || 'AND',
        actions: loadedActions,
        baselineAbsoluteDueDates,
        activeStep: 0
      };
    }
      
    default:
      return state;
  }
}

export function AutomationProvider({ children }) {
  const [state, dispatch] = useReducer(automationReducer, initialState);
  
  // Memoized action creators
  const setActiveStep = useCallback((step) => {
    dispatch({ type: actionTypes.SET_ACTIVE_STEP, payload: step });
  }, []);
  
  const updateWizardData = useCallback((updates) => {
    dispatch({ type: actionTypes.UPDATE_WIZARD_DATA, payload: updates });
  }, []);
  
  const setConditionGroups = useCallback((groups) => {
    dispatch({ type: actionTypes.SET_CONDITION_GROUPS, payload: groups });
  }, []);
  
  const addConditionGroup = useCallback((group) => {
    dispatch({ type: actionTypes.ADD_CONDITION_GROUP, payload: group });
  }, []);
  
  const updateConditionGroup = useCallback((groupId, updates) => {
    dispatch({
      type: actionTypes.UPDATE_CONDITION_GROUP,
      payload: { groupId, updates }
    });
  }, []);
  
  const removeConditionGroup = useCallback((groupId) => {
    dispatch({ type: actionTypes.REMOVE_CONDITION_GROUP, payload: groupId });
  }, []);
  
  const setGroupLogic = useCallback((logic) => {
    dispatch({ type: actionTypes.SET_GROUP_LOGIC, payload: logic });
  }, []);
  
  const setActions = useCallback((actions) => {
    dispatch({ type: actionTypes.SET_ACTIONS, payload: actions });
  }, []);
  
  const addAction = useCallback((action) => {
    dispatch({ type: actionTypes.ADD_ACTION, payload: action });
  }, []);
  
  const updateAction = useCallback((index, updates) => {
    dispatch({
      type: actionTypes.UPDATE_ACTION,
      payload: { index, updates }
    });
  }, []);
  
  const removeAction = useCallback((index) => {
    dispatch({ type: actionTypes.REMOVE_ACTION, payload: index });
  }, []);
  
  const setMetadata = useCallback((type, data) => {
    dispatch({ type: actionTypes.SET_METADATA, payload: { type, data } });
  }, []);
  
  const setLoading = useCallback((updates) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: updates });
  }, []);
  
  const setErrors = useCallback((errors) => {
    dispatch({ type: actionTypes.SET_ERRORS, payload: errors });
  }, []);
  
  const setReferenceData = useCallback((endpoint, data) => {
    dispatch({
      type: actionTypes.SET_REFERENCE_DATA,
      payload: { endpoint, data }
    });
  }, []);
  
  const resetWizard = useCallback(() => {
    dispatch({ type: actionTypes.RESET_WIZARD });
  }, []);
  
  const loadAutomation = useCallback((automationData) => {
    dispatch({ type: actionTypes.LOAD_AUTOMATION, payload: automationData });
  }, []);
  
  const value = {
    state,
    actions: {
      setActiveStep,
      updateWizardData,
      setConditionGroups,
      addConditionGroup,
      updateConditionGroup,
      removeConditionGroup,
      setGroupLogic,
      setActions,
      addAction,
      updateAction,
      removeAction,
      setMetadata,
      setLoading,
      setErrors,
      setReferenceData,
      resetWizard,
      loadAutomation
    }
  };
  
  return (
    <AutomationContext.Provider value={value}>
      {children}
    </AutomationContext.Provider>
  );
}

export function useAutomationContext() {
  const context = useContext(AutomationContext);
  if (!context) {
    throw new Error('useAutomationContext must be used within AutomationProvider');
  }
  return context;
}
